/**
 * Metadata-Driven Navigation System
 * Core type definitions for dynamic, multi-vertical navigation
 */

// ============================================================================
// CORE NAVIGATION TYPES
// ============================================================================

/**
 * Conditional display logic for advanced filtering
 */
export interface ConditionalDisplay {
  when: 'all' | 'any'; // AND vs OR logic
  conditions: Array<{
    type: 'feature_flag' | 'user_property' | 'org_setting' | 'custom';
    key: string;
    operator: 'equals' | 'contains' | 'greater_than' | 'less_than' | 'exists';
    value: any;
  }>;
}

/**
 * Badge configuration for menu items
 */
export interface NavigationBadge {
  type: 'count' | 'dot' | 'text';
  source: 'api_endpoint' | 'realtime_subscription' | 'static';
  endpoint?: string; // API endpoint to fetch count
  color?: string; // Tailwind color class
  value?: string | number; // Static value
}

/**
 * Enhanced Navigation Configuration Item
 * Supports three-layer filtering + advanced features
 */
export interface NavigationConfig {
  // Core identification
  id: string;
  label: string;
  icon?: string; // Lucide React icon name
  
  // Three-layer filtering architecture
  requiredModule?: string | string[]; // Layer 1: Commercial boundary
  requiredPermission?: string | string[]; // Layer 2: Security boundary
  nodeBindingFilter?: string | string[]; // Layer 3: Spatial context boundary
  
  // Advanced filtering (Layer 4)
  conditionalDisplay?: ConditionalDisplay;
  
  // Navigation behavior
  path?: string; // Route path (if leaf node)
  children?: NavigationConfig[]; // Nested items
  contextTransitionMode?: 'replace' | 'merge' | 'stack';
  
  // UI/UX enhancements
  badge?: NavigationBadge;
  description?: string; // Tooltip/help text
  
  // Accessibility
  ariaLabel: string;
  keyboardShortcut?: string; // e.g., "Ctrl+Shift+D"
  mobileLabel?: string; // Shorter label for mobile
  mobileVisibility: 'show' | 'hide' | 'collapse';
  
  // Search & discovery
  searchKeywords: string[];
  searchPriority: number; // Higher = appears first in search
  category: string; // For grouped search results
  
  // Analytics
  telemetryId: string; // Unique tracking identifier
  
  // Performance
  lazyLoad?: boolean; // Load children on expand
  preload?: boolean; // Prefetch route on hover
  
  // Visual styling
  className?: string; // Custom CSS classes
  dividerAfter?: boolean; // Add divider after this item
}

// ============================================================================
// NAVIGATION CONTEXT & STATE
// ============================================================================

/**
 * Current navigation context for evaluation
 */
export interface NavigationContext {
  // User context
  userId: string;
  userPermissions: string[];
  userProperties: Record<string, any>;
  
  // Organization context
  organizationId: string;
  organizationModules: string[]; // Provisioned modules
  organizationSettings: Record<string, any>;
  
  // Spatial context
  selectedNodeId?: string;
  selectedNodeType?: string; // 'agriculture', 'industrial', etc.
  
  // Feature flags
  featureFlags: Record<string, boolean>;
  
  // Session metadata
  sessionId: string;
  timestamp: number;
}

/**
 * Navigation state persistence
 */
export interface NavigationState {
  // UI state
  collapsedSections: string[]; // IDs of collapsed accordion sections
  pinnedItems: string[]; // User-favorited quick access items
  recentlyAccessed: string[]; // Last 5 visited routes (FIFO)
  customOrder?: string[]; // Drag-and-drop reordering
  
  // Context state
  lastActiveContext: {
    organizationId: string;
    nodeId?: string;
    moduleContext: string;
    timestamp: number;
  };
  
  // Preferences
  sidebarCollapsed: boolean;
  mobileSidebarOpen: boolean;
  compactMode: boolean; // Reduced padding/spacing
  
  // Search history
  searchHistory: string[];
}

/**
 * Evaluation result for a navigation item
 */
export interface NavigationEvaluationResult {
  visible: boolean; // Should item be shown?
  enabled: boolean; // Should item be clickable?
  reason?: string; // Why hidden/disabled (for debugging)
  badge?: NavigationBadge; // Dynamic badge data
}

// ============================================================================
// NAVIGATION TREE & RENDERING
// ============================================================================

/**
 * Evaluated navigation tree node (post-filtering)
 */
export interface NavigationTreeNode extends NavigationConfig {
  // Evaluation results
  visible: boolean;
  enabled: boolean;
  
  // Computed properties
  depth: number; // Nesting level (0 = root)
  hasVisibleChildren: boolean;
  isActive: boolean; // Current route matches this item
  isExpanded: boolean; // Accordion state
  
  // Filtered children
  children?: NavigationTreeNode[];
  
  // Runtime data
  badgeValue?: string | number;
}

/**
 * Navigation section grouping
 */
export interface NavigationSection {
  id: string;
  label: string;
  icon?: string;
  items: NavigationTreeNode[];
  collapsible: boolean;
  defaultExpanded: boolean;
  requiredRole?: 'platform_admin' | 'org_admin' | 'user';
}

// ============================================================================
// SEARCH & COMMAND PALETTE
// ============================================================================

/**
 * Search result item
 */
export interface NavigationSearchResult {
  item: NavigationConfig;
  score: number; // Relevance score (0-1)
  matchedKeywords: string[];
  breadcrumb: string[]; // Path to item (e.g., ["Settings", "Security"])
}

/**
 * Command palette action
 */
export interface CommandPaletteAction {
  id: string;
  label: string;
  icon?: string;
  keywords: string[];
  category: string;
  action: () => void | Promise<void>;
  shortcut?: string;
}

// ============================================================================
// ANALYTICS & TELEMETRY
// ============================================================================

/**
 * Navigation usage metrics
 */
export interface NavigationMetrics {
  itemId: string;
  clickCount: number;
  lastAccessed: Date;
  avgTimeOnPage: number; // milliseconds
  bounceRate: number; // % of users who leave immediately
}

/**
 * Navigation event for tracking
 */
export interface NavigationEvent {
  type: 'click' | 'search' | 'context_switch' | 'expand' | 'collapse';
  itemId: string;
  timestamp: number;
  context: NavigationContext;
  metadata?: Record<string, any>;
}

// ============================================================================
// CONFIGURATION & DEFAULTS
// ============================================================================

/**
 * Navigation system configuration
 */
export interface NavigationSystemConfig {
  // Behavior
  enableSearch: boolean;
  enableCommandPalette: boolean;
  enableAnalytics: boolean;
  enableStatePersistence: boolean;
  
  // Performance
  enableMemoization: boolean;
  cacheTimeout: number; // milliseconds
  lazyLoadThreshold: number; // Number of items before lazy loading
  
  // UI
  maxDepth: number; // Maximum nesting level
  mobileBreakpoint: number; // px
  animationDuration: number; // milliseconds
  
  // Fallbacks
  onModuleLoadError: 'show_base_mvp0_only' | 'show_last_known' | 'show_error';
  onPermissionLoadError: 'show_read_only_view' | 'show_last_known' | 'show_error';
  onContextLoadError: 'show_last_known_state' | 'show_default' | 'show_error';
  offlineMode: 'cache_last_successful_load' | 'show_offline_message';
}

/**
 * Default navigation system configuration
 */
export const DEFAULT_NAVIGATION_CONFIG: NavigationSystemConfig = {
  enableSearch: true,
  enableCommandPalette: true,
  enableAnalytics: true,
  enableStatePersistence: true,
  enableMemoization: true,
  cacheTimeout: 300000, // 5 minutes
  lazyLoadThreshold: 50,
  maxDepth: 4,
  mobileBreakpoint: 768,
  animationDuration: 200,
  onModuleLoadError: 'show_base_mvp0_only',
  onPermissionLoadError: 'show_read_only_view',
  onContextLoadError: 'show_last_known_state',
  offlineMode: 'cache_last_successful_load',
};

// Made with Bob
